import openWindow from "./openWindow";
import type WindowFeaturesOptions from "../store/schema/WindowFeaturesOptions";
import { getItem, removeItem } from "owa-local-storage";
import { trace } from "owa-trace";
import type { vDirType } from "owa-vdir-types";
import { lazySavePopoutWindowGeometry, lazyTrackPopoutWindow } from "../lazyFunctions";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

export type PopoutWindowType = `${"mail" | "calendar"}-${"compose" | "read"}`;
// Persist outer bounds, which do not change with browser page zoom.
type WindowGeometry = Pick<WindowFeaturesOptions, "left" | "top" | "width" | "height">;

const MINIMIZED_WINDOW_POSITION = -32000;

export function getPopoutWindowType(vdir: vDirType, route: string): PopoutWindowType | undefined {
    if (vdir !== "mail" && vdir !== "calendar") {
        return undefined;
    }
    
    const routeType = route.split("/")[0];
    if (routeType === "compose") {
        return `${vdir}-compose`;
    }
    else if (["read", "readconv", "readimid"].includes(routeType)) {
        return `${vdir}-read`;
    }
    return undefined;
}

export default function openPopoutWindow(url: string, options: Partial<WindowFeaturesOptions> | undefined, parentWindow: Window | undefined, type: PopoutWindowType | undefined): Window | null {
    if (!isGlobalFeatureEnabled("fwk-popout-window-geometry")) {
        return openWindow(url, options, parentWindow);
    }
    
    let geometry: WindowGeometry | undefined;
    if (type) {
        if (lazySavePopoutWindowGeometry.isImportLoaded()) {
            lazySavePopoutWindowGeometry.dangerouslyImportSync()(type);
        }
        geometry = getSavedGeometry(type);
        if (geometry) {
            options = { ...options, ...geometry };
        }
    }
    
    const childWindow = openWindow(url, options, parentWindow);
    if (childWindow && childWindow !== window && type) {
        if (geometry &&
            (childWindow.outerWidth !== geometry.width ||
                childWindow.outerHeight !== geometry.height)) {
            // window.open sizes the content area; resizeTo includes the browser frame.
            childWindow.resizeTo(geometry.width, geometry.height);
            // Reapply position after resizing, since the initial size may have been clamped.
            childWindow.moveTo(geometry.left, geometry.top);
        }
        lazyTrackPopoutWindow
            .importAndExecute(childWindow, type)
            .catch(error => trace.warn("Unable to start popout window geometry tracking: " + error));
    }
    return childWindow;
}

function getSavedGeometry(type: PopoutWindowType): WindowGeometry | undefined {
    const key = `PopoutWindowGeometry_${type}` as const;
    const serialized = getItem(window, key);
    if (serialized === null) {
        return undefined;
    }
    
    try {
        const geometry: unknown = JSON.parse(serialized);
        if (isWindowGeometry(geometry)) {
            return {
                left: geometry.left,
                top: geometry.top,
                width: geometry.width,
                height: geometry.height,
            };
        }
    }
    catch {
        trace.warn("Unable to parse saved popout window geometry");
        removeItem(window, key);
        return undefined;
    }
    
    trace.warn("Discarding invalid saved popout window geometry");
    removeItem(window, key);
    return undefined;
}

export function isWindowGeometry(value: unknown): value is WindowGeometry {
    return (typeof value === "object" &&
        value !== null &&
        "left" in value &&
        isFiniteNumber(value.left) &&
        value.left !== MINIMIZED_WINDOW_POSITION &&
        "top" in value &&
        isFiniteNumber(value.top) &&
        value.top !== MINIMIZED_WINDOW_POSITION &&
        "width" in value &&
        isFiniteNumber(value.width) &&
        value.width > 0 &&
        "height" in value &&
        isFiniteNumber(value.height) &&
        value.height > 0);
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value);
}
